import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';

export const TransactionFormDialog = ({
  open,
  onOpenChange,
  selectedTransaction,
  formData,
  setFormData,
  isReservation,
  setIsReservation,
  showQuickSheepForm,
  setShowQuickSheepForm,
  quickSheepData,
  setQuickSheepData,
  autoSheepNumber,
  setAutoSheepNumber,
  onFetchNextSheepNumber,
  onSubmit,
  sheep,
  breeds,
  currencySymbol,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{selectedTransaction ? 'Modifier une transaction' : 'Ajouter une transaction'}</DialogTitle>
        <DialogDescription>
          {selectedTransaction ? 'Modifiez les informations de cette transaction' : 'Enregistrez une vente, un achat ou une reservation'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type de transaction *</Label>
            <Select value={formData.type} onValueChange={(value) => {
              setFormData({ ...formData, type: value, sheep_id: '' });
              if (value !== 'vente') setIsReservation(false);
            }}>
              <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vente">Vente</SelectItem>
                <SelectItem value="achat">Achat</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="transaction-date-input" />
          </div>
        </div>

        {formData.type === 'vente' && !selectedTransaction && (
          <div className="border rounded-lg p-4 bg-amber-50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Reserver ce mouton</Label>
                <p className="text-sm text-gray-500 mt-1">Le mouton sera mis en statut "reserve" avec possibilite de finaliser la vente plus tard</p>
              </div>
              <button
                type="button"
                onClick={() => setIsReservation(!isReservation)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isReservation ? 'bg-amber-500' : 'bg-gray-300'}`}
                data-testid="reservation-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isReservation ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="sheep">{formData.type === 'vente' ? 'Mouton *' : 'Mouton (optionnel)'}</Label>
          <Select value={formData.sheep_id || "none"} onValueChange={(value) => setFormData({ ...formData, sheep_id: value === "none" ? "" : value })}>
            <SelectTrigger data-testid="transaction-sheep-select"><SelectValue placeholder="Selectionner un mouton" /></SelectTrigger>
            <SelectContent>
              {formData.type !== 'vente' && <SelectItem value="none">Aucun</SelectItem>}
              {sheep.filter((s) => s.statut === 'actif').sort((a, b) => (a.numero || '').localeCompare(b.numero || '', undefined, { numeric: true })).map((s) => (
                <SelectItem key={s.id} value={s.id}>N {s.numero} {s.nom && `- ${s.nom}`}{s.poids ? ` (${s.poids} kg)` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.type === 'achat' && (
            <div className="mt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowQuickSheepForm(!showQuickSheepForm)} className="text-sm">
                {showQuickSheepForm ? 'Masquer' : 'Ajouter un nouveau mouton'}
              </Button>
            </div>
          )}
        </div>

        {formData.type === 'achat' && showQuickSheepForm && (
          <div className="p-4 bg-blue-50 rounded-lg space-y-4">
            <h3 className="font-medium text-blue-900">Nouveau mouton a ajouter</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="quick-numero">Numero *</Label>
                  <button
                    type="button"
                    onClick={() => {
                      const newAutoState = !autoSheepNumber;
                      setAutoSheepNumber(newAutoState);
                      if (newAutoState) onFetchNextSheepNumber();
                    }}
                    className={`text-xs px-2 py-1 rounded font-medium transition-colors ${autoSheepNumber ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {autoSheepNumber ? 'Auto' : 'Manuel'}
                  </button>
                </div>
                <Input
                  id="quick-numero"
                  value={quickSheepData.numero}
                  onChange={(e) => { setQuickSheepData({ ...quickSheepData, numero: e.target.value }); setAutoSheepNumber(false); }}
                  placeholder={autoSheepNumber ? 'Numero genere automatiquement' : 'Ex: 0001'}
                  data-testid="quick-sheep-numero-input"
                  className={autoSheepNumber ? 'bg-blue-50 border-blue-200' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-nom">Nom</Label>
                <Input id="quick-nom" value={quickSheepData.nom} onChange={(e) => setQuickSheepData({ ...quickSheepData, nom: e.target.value })} placeholder="Nom du mouton" data-testid="quick-sheep-nom-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quick-sexe">Sexe *</Label>
                <Select value={quickSheepData.sexe} onValueChange={(value) => setQuickSheepData({ ...quickSheepData, sexe: value })}>
                  <SelectTrigger data-testid="quick-sheep-sexe-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="femelle">Femelle</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-race">Race *</Label>
                <Select value={quickSheepData.race} onValueChange={(value) => setQuickSheepData({ ...quickSheepData, race: value })}>
                  <SelectTrigger data-testid="quick-sheep-race-select"><SelectValue placeholder="Selectionner une race" /></SelectTrigger>
                  <SelectContent>
                    {breeds.map((breed) => (<SelectItem key={breed.id} value={breed.nom}>{breed.nom}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-blue-600">Ce mouton sera automatiquement cree avec cette transaction d'achat</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="prix">{isReservation ? `Prix estime (${currencySymbol})` : `Prix total * (${currencySymbol})`}</Label>
            <Input id="prix" type="number" step="0.01" min="0" value={formData.prix} onChange={(e) => setFormData({ ...formData, prix: e.target.value })} required={!isReservation} placeholder={isReservation ? 'Optionnel' : ''} data-testid="transaction-prix-input" />
            {isReservation && <p className="text-xs text-gray-500">Le prix final sera confirme lors de la finalisation</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact">Contact</Label>
            <Input id="contact" value={formData.contact} onChange={(e) => setFormData({ ...formData, contact: e.target.value })} placeholder="Nom du client/fournisseur" data-testid="transaction-contact-input" />
          </div>
        </div>

        {isReservation && (
          <div className="space-y-2 border rounded-lg p-4 bg-amber-50">
            <Label htmlFor="acompte" className="flex items-center gap-2">Acompte ({currencySymbol})</Label>
            <Input id="acompte" type="number" step="0.01" min="0" max={formData.prix || 999999} value={formData.acompte} onChange={(e) => setFormData({ ...formData, acompte: e.target.value })} placeholder="Montant de l'acompte" data-testid="transaction-acompte-input" />
            <p className="text-xs text-amber-700">Reste a payer: {((parseFloat(formData.prix) || 0) - (parseFloat(formData.acompte) || 0)).toFixed(2)} {currencySymbol}</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Details supplementaires..." data-testid="transaction-notes-input" />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            className={`flex-1 ${isReservation ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600'}`}
            data-testid="transaction-submit-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {selectedTransaction ? 'Modifier la transaction' : isReservation ? 'Enregistrer la reservation' : 'Ajouter la transaction'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
